/**
 * 認証 API エンドポイント定義
 *
 * 【このファイルの役割】
 * サーバーの認証 API（/auth/login, /auth/logout, /auth/me, /auth/password）を
 * RTK Query のエンドポイントとして定義する。
 *
 * 定義すると、自動で React hooks が生成される：
 * - useLoginMutation()     → ログイン API を呼ぶ hook
 * - useLogoutMutation()    → ログアウト API を呼ぶ hook
 * - useGetMeQuery()        → 認証済みユーザー情報を取得する hook
 * - useChangePasswordMutation() → パスワード変更 API を呼ぶ hook
 *
 * 【api.injectEndpoints() とは？】
 * shared/store/api.ts で作った createApi の「空の endpoints」に、
 * 後から認証系のエンドポイントを「注入」する仕組み。
 * ファイルを分けて管理できるのが利点。
 *
 * 【invalidatesTags とは？】
 * mutation（データ変更）が成功したときに、
 * 関連する query（データ取得）のキャッシュを無効化する仕組み。
 * 例：login 成功 → 'Auth' タグのキャッシュ無効化 → getMe が再取得される
 */

import { api } from '../../store/api';

// ---------- 型定義 ----------

/** ログインレスポンスに含まれるユーザー情報 */
interface AuthUser {
  id: number;
  code: string;
  name: string;
  role: string | null;
  permission_level: number;
  admin_level: number;
  must_change_password: boolean;
  privacy_consent_at: string | null;
}

/** GET /auth/me のレスポンス（より詳細なユーザー情報） */
interface MeResponse {
  id: number;
  code: string;
  name: string;
  tenant_id: string;
  tenant_code: string;
  store_id: number;
  department_id: number;
  role: string | null;
  permission_level: number;
  admin_level: number;
  must_change_password: boolean;
  privacy_consent_at: string | null;
}

/** POST /auth/login のリクエストボディ */
interface LoginRequest {
  code: string;
  password: string;
}

/** POST /auth/login のレスポンス（Web版：トークンは Cookie で受け取るので Body にはユーザー情報のみ） */
interface LoginResponse {
  user: AuthUser;
}

/** PUT /auth/password のリクエストボディ */
interface PasswordChangeRequest {
  current_password: string;
  new_password: string;
}

// ---------- エンドポイント定義 ----------

export const authApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * ログイン
     * POST /auth/login に { code, password } を送る。
     * サーバーが Set-Cookie でトークンを設定するので、
     * フロントは Cookie を意識する必要がない。
     */
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
      // ログイン成功時に 'Auth' タグのキャッシュを無効化
      // → getMe が再取得され、最新のユーザー情報が反映される
      invalidatesTags: ['Auth'],
    }),

    /**
     * ログアウト
     * POST /auth/logout でサーバー側のセッションを破棄。
     * Web の場合、Body は不要（Cookie が自動送信される）。
     */
    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
    }),

    /**
     * 認証済みユーザー情報の取得
     * GET /auth/me で「今ログインしているユーザーは誰か？」を確認する。
     *
     * 用途：
     * - アプリ起動時に「まだログイン済みか？」を確認
     * - 5分ごとにポーリングしてセッション有効性をチェック
     *
     * providesTags: ['Auth'] により、login/logout 時に自動で再取得される。
     */
    getMe: builder.query<MeResponse, void>({
      query: () => '/auth/me',
      providesTags: ['Auth'],
    }),

    /**
     * パスワード変更（Phase 3 で使用）
     * PUT /auth/password に { current_password, new_password } を送る。
     */
    changePassword: builder.mutation<{ message: string }, PasswordChangeRequest>({
      query: (body) => ({
        url: '/auth/password',
        method: 'PUT',
        body,
      }),
    }),

    /**
     * 個人情報取り扱い同意
     * POST /auth/privacy-consent で同意日時を記録する。
     */
    acceptPrivacyConsent: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: '/auth/privacy-consent',
        method: 'POST',
      }),
      invalidatesTags: ['Auth'],
    }),
  }),
});

/**
 * 自動生成された React hooks
 * コンポーネントでこれらを import して使う。
 *
 * 使い方の例：
 *   const [login, { isLoading }] = useLoginMutation();
 *   const { data: user, isLoading } = useGetMeQuery();
 */
export const {
  useLoginMutation,
  useLogoutMutation,
  useGetMeQuery,
  useChangePasswordMutation,
  useAcceptPrivacyConsentMutation,
} = authApi;
